import { Button, Dialog, FormControlLabel, MenuItem, styled, Switch, SwitchProps, TextField, InputAdornment, IconButton } from "@mui/material"
import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Box from "elements/Box";
import {ProjectConfig} from 'Global';
import { useSnackbar, withSnackbar } from "notistack";
import ISwitch from "elements/ISwitch";
import AutocompleteSuppliers from "elements/AutocompleteSuppliers";
import AutocompleteWarehouses from "elements/AutocompleteWarehouses";
import AutocompleteParentProducts from "elements/AutocompleteParentProducts";
import AutocompleteBrands from "elements/AutocompleteBrands";
import AutocompleteCategories from "elements/AutocompleteCategories";
import { ApiRequest } from "GlobalFunctions";
import ImageUploader from "elements/ImageUploader";
import { PiEyeLight } from "react-icons/pi";
import { PiEyeSlashLight } from "react-icons/pi";

export default function ProductEditor(props : any) {
  const {
    setLoading, 
    openEditor, 
    setOpenEditor, 
    product_id,
    loadList
  } = props;

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const defaultValues: any = {
    product_id: product_id ? product_id : "null",
    parent_product_id: "",
    product_name: "",
    product_code: "",
    product_type: "venta",
    cost: "",
    profit_margin: "",
    base_price: "",
    price_1: "",
    price_2: "",
    price_3: "",
    product_existences: "",
    product_package: "",
    product_conversion: "",
    product_measure_type: "",
    supplier_id: "",
    warehouse_id: "",
    product_warehouse_order: "",
    unit_measure: "",
    brand_id: "",
    category_id: "",
    barcode: "",
    tax_1: 0,
    tax_2: 0,
    tax_2_percentage: 0,
    custom_taxes: [
      { tax_number: 1, amount: 0, percentage: 0 },
      { tax_number: 2, amount: 0, percentage: 0 },
      { tax_number: 3, amount: 0, percentage: 0 },
      { tax_number: 4, amount: 0, percentage: 0 },
      { tax_number: 5, amount: 0, percentage: 0 },
    ],
    max_discount: 0,
    has_custom_max_discount: false,
    has_max_sale_limit: 0,
    max_sale_limit: 0,
    is_active: 1,
    is_new: 0,
    is_promo: 0,
    is_recommended: 0,
    inventory: [],
    inventory_total: 0,
    image: '',
  };
  
  const [formValues, setFormValues] = useState(defaultValues)

  const [files, setFiles] = useState<any>([]);

  // Price editing states
  const [pricesEditable, setPricesEditable] = useState(false);
  const [initialPrices, setInitialPrices] = useState<any>({
    cost: "",
    profit_margin: "",
    base_price: "",
    price_1: "",
    price_2: "",
    price_3: "",
    max_discount: "",
  });

  // Master password dialog
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [masterPassword, setMasterPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (product_id) {
      axios.get(ProjectConfig.api_url + "/products/details", {
          params: {
            product_id: product_id,
          },
        })
        .then((response) => {
          console.log("Product details", response.data)
          setFormValues(response.data.data);
          // Store initial prices
          setInitialPrices({
            cost: response.data.data.cost,
            profit_margin: response.data.data.profit_margin,
            base_price: response.data.data.base_price,
            price_1: response.data.data.price_1,
            price_2: response.data.data.price_2,
            price_3: response.data.data.price_3,
            max_discount: response.data.data.max_discount,
            has_custom_max_discount: response.data.data.has_custom_max_discount,
          });
        })
        .catch((err) => {
          console.log(err.response.data);
        });
    }
    else{
      setFormValues(defaultValues);
      setInitialPrices({
        cost: "",
        profit_margin: "",
        base_price: "",
        price_1: "",
        price_2: "",
        price_3: "",
        max_discount: "",
        has_custom_max_discount: false,
      });
    }

  } , [product_id])

  const handleInputChange = (e: { target: { name: any; value: any; }; }) => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value,
    });
  };

  useEffect(() => {
    const calculateUnitMeasure = () => {
      const existences = parseInt(formValues.product_existences, 10);
      const packageSize = parseInt(formValues.product_package, 10);
      if (!isNaN(existences) && !isNaN(packageSize) && packageSize > 0) {
        const packages = Math.floor(existences / packageSize);
        const units = existences % packageSize;
        if (units === 0) {
          return `${packages} empaques`;
        } else {
          return `${packages} empaques y ${units} unidades`;
        }
      }
      return formValues.unit_measure; // Use current value instead of returning an empty string to avoid undefined
    };
  
    const unitMeasure = calculateUnitMeasure();
    if (formValues.unit_measure !== unitMeasure) {
      setFormValues((prevValues: any) => ({
        ...prevValues,
        unit_measure: unitMeasure,
      }));
    }
  }, [formValues.product_existences, formValues.product_package, formValues.unit_measure]);
  
  /*=======================================
  HANDLE SUBMIT
  =======================================*/

  // Check if prices or max_discount have changed
  const havePricesChanged = () => {
    return (
      formValues.cost !== initialPrices.cost ||
      formValues.profit_margin !== initialPrices.profit_margin ||
      formValues.base_price !== initialPrices.base_price ||
      formValues.price_1 !== initialPrices.price_1 ||
      formValues.price_2 !== initialPrices.price_2 ||
      formValues.price_3 !== initialPrices.price_3 ||
      formValues.max_discount !== initialPrices.max_discount ||
      formValues.has_custom_max_discount !== initialPrices.has_custom_max_discount
    );
  };

  const handleSubmitClick = () => {
    const maxSaleLimitValue = Number(formValues.max_sale_limit);
    if (formValues.has_max_sale_limit && (!Number.isFinite(maxSaleLimitValue) || maxSaleLimitValue <= 0)) {
      enqueueSnackbar("La cantidad maxima de venta debe ser mayor a 0", { variant: "error" });
      return;
    }
    // If prices changed, require master password
    if (havePricesChanged()) {
      setOpenPasswordDialog(true);
    } else {
      // Save directly without password validation
      handleSubmit();
    }
  };

  const handleSubmit = async (password?: string) => {
    console.log("formValues", formValues);
    setLoading(true);
    const body = new FormData();
    body.append("formValues", JSON.stringify(formValues));

    // Add master password if prices changed
    if (password) {
      body.append("master_password", password);
    }

    if (files) {
      body.append("image", files);
    }

    await ApiRequest({
      url: "/products/addedit",
      method: "post",
      headers: {
        ltkn: localStorage.getItem("ltkn"),
      },
      body: body,
      setResponse: (response: any) => {
        if (response.status === "success") {
          enqueueSnackbar("Guardado con éxito", { variant: "success" });
          setOpenEditor(false);
          setOpenPasswordDialog(false);
          setMasterPassword("");
          setPricesEditable(false);
          loadList();
        } else {
          enqueueSnackbar(response.message, { variant: "error" });
        }
        setLoading(false);
      },
    });
  };

  return (
      <>
        <Dialog
          open={openEditor}
          onClose={() => {
            setOpenEditor(false);
            setPricesEditable(false);
          }}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <div className="DialogContainer" style={{minWidth:'400px'}}>
              <div className="ModuleWrapper">
                  <div className="boxContainerHeader">
                      <div className="boxContainerTitle">
                          <h2 className="h2_title">Producto</h2>
                          <span className="h2_subtitle">Editar</span>
                      </div>
                  </div>

                  <div className="input_group">

                    <AutocompleteParentProducts
                      value={formValues.parent_product_id}
                      onChange={(e: any) => {
                        setFormValues({
                          ...formValues,
                          parent_product_id: e.target.value,
                        });
                      }}
                    />

                    <TextField
                      id="product_name"
                      name="product_name"
                      label="Nombre del Producto"
                      type="text"
                      required
                      fullWidth
                      value={formValues.product_name}
                      onChange={handleInputChange}
                    />
                      <TextField
                        id="product_code"
                        name="product_code"
                        label="Código del Producto"
                        type="text"
                        required
                        fullWidth
                        value={formValues.product_code}
                        onChange={handleInputChange}
                      />

                      <TextField
                        id="product_type"
                        name="product_type"
                        label="Tipo de Producto"
                        select
                        fullWidth
                        value={formValues.product_type || "venta"}
                        onChange={handleInputChange}
                      >
                        <MenuItem value="venta">Venta</MenuItem>
                        <MenuItem value="abono">Abono</MenuItem>
                        <MenuItem value="combo">Combo</MenuItem>
                        <MenuItem value="temporada">Temporada</MenuItem>
                      </TextField>

                      <div className="input_row">
                        <AutocompleteBrands
                          value={formValues.brand_id}
                          onChange={(e: any) => {
                            setFormValues({
                              ...formValues,
                              brand_id: e.target.value,
                            });
                          }}
                        />

                        <AutocompleteCategories 
                          value={formValues.category_id}
                          onChange={(e: any) => {
                            setFormValues({
                              ...formValues,
                              category_id: e.target.value,
                            });
                          }}
                        />
                      </div>

                    <div style={{ backgroundColor: '#f5f5f5', padding: '15px', borderRadius: '8px', marginBottom: '15px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <h3 style={{ margin: 0 }}>Precios</h3>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => setPricesEditable(!pricesEditable)}
                        >
                          {pricesEditable ? 'Bloquear Precios' : 'Editar Precios'}
                        </Button>
                      </div>
                      <div className="input_row" style={{ marginBottom: '15px' }}>

                        <TextField
                          id="cost"
                          name="cost"
                          label="Costo"
                          type="text"
                          fullWidth
                          value={formValues.cost}
                          onChange={handleInputChange}
                          disabled={!pricesEditable}
                        />

                        <TextField
                          id="profit_margin"
                          name="profit_margin"
                          label="% Utilidad"
                          type="text"
                          fullWidth
                          value={formValues.profit_margin}
                          onChange={handleInputChange}
                          disabled={!pricesEditable}
                        />
                      </div>
                      <div className="input_row">

                        <TextField
                          id="base_price"
                          name="base_price"
                          label="Precio Base"
                          type="text"
                          required
                          fullWidth
                          value={formValues.base_price}
                          onChange={handleInputChange}
                          disabled={!pricesEditable}
                        />

                        <TextField
                          id="price_1"
                          name="price_1"
                          label="Precio 1"
                          type="text"
                          required
                          fullWidth
                          value={formValues.price_1}
                          onChange={handleInputChange}
                          disabled={!pricesEditable}
                        />

                        <TextField
                          id="price_2"
                          name="price_2"
                          label="Precio 2"
                          type="text"
                          required
                          fullWidth
                          value={formValues.price_2}
                          onChange={handleInputChange}
                          disabled={ !pricesEditable}
                        />

                        <TextField
                          id="price_3"
                          name="price_3"
                          label="Precio 3"
                          type="text"
                          required
                          fullWidth
                          value={formValues.price_3}
                          onChange={handleInputChange}
                          disabled={ !pricesEditable}
                        />
                      </div>

                      <h3 style={{ marginTop: '15px', marginBottom: '10px' }}>Descuento Máximo por Producto</h3>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formValues.has_custom_max_discount}
                            onChange={(e: any) => {
                              setFormValues({
                                ...formValues,
                                has_custom_max_discount: e.target.checked,
                                max_discount: e.target.checked ? formValues.max_discount : 0,
                              });
                            }}
                            disabled={!pricesEditable}
                          />
                        }
                        label="Habilitar descuento máximo personalizado"
                      />
                      {formValues.has_custom_max_discount && (
                        <TextField
                          id="max_discount"
                          name="max_discount"
                          label="Descuento Máximo (%)"
                          type="number"
                          fullWidth
                          value={formValues.max_discount}
                          onChange={handleInputChange}
                          inputProps={{ min: 0 }}
                          disabled={!pricesEditable}
                          style={{ marginTop: '10px' }}
                        />
                      )}

                      <h3 style={{ marginTop: '15px', marginBottom: '10px' }}>Impuestos</h3>
                      <div className="input_row">
                        <TextField
                          id="tax_1"
                          name="tax_1"
                          label="IVA (%)"
                          type="text"
                          required
                          fullWidth
                          value={formValues.tax_1}
                          onChange={handleInputChange}
                          disabled={!pricesEditable}
                        />

                        <TextField
                          id="tax_2"
                          name="tax_2"
                          label="Ipoconsumo ($)"
                          type="text"
                          required
                          fullWidth
                          value={formValues.tax_2}
                          onChange={handleInputChange}
                          disabled={!pricesEditable}
                        />

                        <TextField
                          id="tax_2_percentage"
                          name="tax_2_percentage"
                          label="Ipoconsumo (%)"
                          type="text"
                          fullWidth
                          value={formValues.tax_2_percentage}
                          onChange={handleInputChange}
                          disabled={!pricesEditable}
                        />
                      </div>

                      <h3 style={{ marginTop: '15px', marginBottom: '10px' }}>Impuestos Personalizados</h3>
                      {formValues.custom_taxes && formValues.custom_taxes.map((tax: any, index: number) => (
                        <div className="input_row" key={tax.tax_number} style={{ marginBottom: '10px' }}>
                          <TextField
                            label={`Impuesto ${tax.tax_number} ($)`}
                            type="text"
                            fullWidth
                            value={tax.amount}
                            onChange={(e: any) => {
                              const newCustomTaxes = [...formValues.custom_taxes];
                              newCustomTaxes[index] = { ...newCustomTaxes[index], amount: e.target.value };
                              setFormValues({ ...formValues, custom_taxes: newCustomTaxes });
                            }}
                            disabled={!pricesEditable}
                          />
                          <TextField
                            label={`Impuesto ${tax.tax_number} (%)`}
                            type="text"
                            fullWidth
                            value={tax.percentage}
                            onChange={(e: any) => {
                              const newCustomTaxes = [...formValues.custom_taxes];
                              newCustomTaxes[index] = { ...newCustomTaxes[index], percentage: e.target.value };
                              setFormValues({ ...formValues, custom_taxes: newCustomTaxes });
                            }}
                            disabled={!pricesEditable}
                          />
                        </div>
                      ))}
                    </div>

                    {/* Precio + Impuestos (informativo) - COMENTADO
                    {(() => {
                      const calcTotal = (price: number) => {
                        if (!price) return 0;
                        let tax = 0;
                        if (formValues.tax_1 > 0) tax += Math.round(formValues.tax_1 * price / 100);
                        if (formValues.tax_2_percentage > 0) tax += Math.round(formValues.tax_2_percentage * price / 100);
                        else if (formValues.tax_2 > 0) tax += Math.round(Number(formValues.tax_2));
                        if (formValues.custom_taxes) {
                          formValues.custom_taxes.forEach((t: any) => {
                            if (t.percentage > 0) tax += Math.round(t.percentage * price / 100);
                            else if (t.amount > 0) tax += Math.round(Number(t.amount));
                          });
                        }
                        return price + tax;
                      };
                      const prices = [
                        { label: 'Precio 1', value: Number(formValues.price_1) },
                        { label: 'Precio 2', value: Number(formValues.price_2) },
                        { label: 'Precio 3', value: Number(formValues.price_3) },
                      ].filter(p => p.value > 0);
                      if (prices.length === 0) return null;
                      return (
                        <div style={{ backgroundColor: '#e8f5e9', padding: '12px 15px', borderRadius: '8px', marginBottom: '15px' }}>
                          <h3 style={{ margin: '0 0 8px 0', fontSize: '14px' }}>Precio + Impuestos</h3>
                          {prices.map(p => (
                            <div key={p.label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                              <span>{p.label}:</span>
                              <strong>${calcTotal(p.value).toLocaleString()}</strong>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                    */}

                    <TextField
                      id="product_package"
                      name="product_package"
                      label="Empaque"
                      type="text"
                      required
                      fullWidth
                      value={formValues.product_package}
                      onChange={handleInputChange}
                    />

                    <TextField
                      id="product_conversion"
                      name="product_conversion"
                      label="Conversión"
                      type="text"
                      required
                      fullWidth
                      value={formValues.product_conversion}
                      onChange={handleInputChange}
                    />

                    <TextField
                      id="product_measure_type"
                      name="product_measure_type"
                      label="Unidad de Medida"
                      type="text"
                      required
                      fullWidth
                      value={formValues.product_measure_type}
                      onChange={handleInputChange}
                    />
                    
                    <AutocompleteSuppliers
                      value={formValues.supplier_id}
                      onChange={(e: any) => {
                        setFormValues({
                          ...formValues,
                          supplier_id: e.target.value,
                        });
                      }}
                    />

                    <AutocompleteWarehouses
                      value={formValues.warehouse_id}
                      onChange={(e: any) => {
                        setFormValues({
                          ...formValues,
                          warehouse_id: e.target.value,
                        });
                      }}
                    />

                    <TextField
                      id="product_warehouse_order"
                      name="product_warehouse_order"
                      label="Orden de bodega"
                      type="text"
                      required
                      fullWidth
                      value={formValues.product_warehouse_order}
                      onChange={handleInputChange}
                    />

                    <TextField
                      id="barcode"
                      name="barcode"
                      label="Código de Barras"
                      type="text"
                      required
                      fullWidth
                      value={formValues.barcode}
                      onChange={handleInputChange}
                    />

                    <h3>Limite de Venta</h3>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!formValues.has_max_sale_limit}
                          onChange={(e: any) => {
                            const enableLimit = e.target.checked;
                            const currentLimit = Number(formValues.max_sale_limit);
                            const nextLimit = enableLimit && currentLimit <= 0 ? 1 : (enableLimit ? currentLimit : 0);
                            setFormValues({
                              ...formValues,
                              has_max_sale_limit: enableLimit ? 1 : 0,
                              max_sale_limit: nextLimit,
                            });
                          }}
                        />
                      }
                      label="Limitar cantidad maxima de venta"
                    />
                    {formValues.has_max_sale_limit ? (
                      <TextField
                        id="max_sale_limit"
                        name="max_sale_limit"
                        label="Cantidad maxima"
                        type="number"
                        fullWidth
                        value={formValues.max_sale_limit}
                        onChange={handleInputChange}
                        inputProps={{ min: 1 }}
                      />
                    ) : null}

                    <FormControlLabel
                      control={
                        <ISwitch
                          checked={formValues.is_active}
                          onChange={(e: any) => {
                            setFormValues({
                              ...formValues,
                              is_active: e.target.checked,
                            });
                          }}
                          name="is_active"
                        />
                      }
                      label="Activo"
                    />


                    <h3>Estado</h3>
                    <div className="input_row">
                      <FormControlLabel
                        control={
                          <ISwitch
                            checked={formValues.is_new}
                            onChange={(e: any) => {
                              setFormValues({
                                ...formValues,
                                is_new: e.target.checked ? 1 : 0,
                              });
                            }}
                            name="is_new"
                          />
                        }
                        label="Producto Nuevo"
                      />

                      <FormControlLabel
                        control={
                          <ISwitch
                            checked={formValues.is_promo}
                            onChange={(e: any) => {
                              setFormValues({
                                ...formValues,
                                is_promo: e.target.checked ? 1 : 0,
                              });
                            }}
                            name="is_promo"
                          />
                        }
                        label="Promoción"
                      />

                      <FormControlLabel
                        control={
                          <ISwitch
                            checked={formValues.is_recommended}
                            onChange={(e: any) => {
                              setFormValues({
                                ...formValues,
                                is_recommended: e.target.checked ? 1 : 0,
                              });
                            }}
                            name="is_recommended"
                          />
                        }
                        label="Recomendado"
                      />
                    </div>

                    <h3>Inventario</h3>
                    {formValues.inventory.map((inv: any, index: number) => (
                      <TextField
                        key={inv.warehouse_id}
                        label={inv.warehouse_name}
                        id={`stock_${inv.warehouse_id}`}
                        name={`stock_${inv.warehouse_id}`}
                        type="text"
                        value={inv.stock}
                        onChange={(e: any) => {
                          const newInventory = formValues.inventory.map((item: any) => {
                            if (item.warehouse_id === inv.warehouse_id) {
                              return {
                                ...item,
                                stock: e.target.value,
                              };
                            }
                            return item;
                          });
                          const newTotal = newInventory.reduce((acc: number, item: any) => {
                            const val = parseFloat(item.stock);
                            return acc + (isNaN(val) ? 0 : val);
                          }, 0);
                          setFormValues({
                            ...formValues,
                            inventory: newInventory,
                            inventory_total: newTotal,
                          });
                        }}
                      />
                    ))}
                    <div className="inventory_row">
                      <h3>Total existencias</h3> 
                      <label>{Number(formValues.inventory_total).toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</label>
                    </div>
                    <ImageUploader 
                      className="product_image"
                      image={formValues.image} 
                      setImage={(newImage) => setFormValues({ ...formValues, image: newImage })}
                      setFiles={setFiles} 
                    />
                  </div>
                </div>
              </div>
         
          <div className="dialogButtons">
            <Button
                onClick={() => {
                  setOpenEditor(false);
                  setPricesEditable(false);
                }}
                variant="outlined"
                className="button_2"
            >
              Cancelar
            </Button>

            <Button onClick={handleSubmitClick} variant="contained" className="button_1">Guardar</Button>
          </div>

        </Dialog>

        {/* Master Password Dialog */}
        <Dialog
          open={openPasswordDialog}
          onClose={() => {
            setOpenPasswordDialog(false);
            setMasterPassword("");
            setShowPassword(false);
          }}
        >
          <div className="DialogContainer" style={{maxWidth:'400px'}}>
            <div className="ModuleWrapper">
              <div className="boxContainerHeader">
                <div className="boxContainerTitle">
                  <h2 className="h2_title">Verificación de Seguridad</h2>
                  <span className="h2_subtitle">
                    Ingrese la contraseña maestra para guardar cambios en los precios
                  </span>
                </div>
              </div>

              <div id="userForm">
                <div className="userForm_right">
                  <div className='input_group'>
                    <TextField
                      id="master_password"
                      name="master_password"
                      label="Contraseña Maestra"
                      type={showPassword ? "text" : "password"}
                      fullWidth
                      autoComplete="off"
                      value={masterPassword}
                      onChange={(e) => setMasterPassword(e.target.value)}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                              size="small"
                            >
                              {showPassword ? <PiEyeSlashLight size={20} /> : <PiEyeLight size={20} />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="dialogButtons">
            <Button
              onClick={() => {
                setOpenPasswordDialog(false);
                setMasterPassword("");
                setShowPassword(false);
              }}
              variant="outlined"
              className="button_2"
            >
              Cancelar
            </Button>

            <Button
              onClick={() => {
                if (!masterPassword) {
                  enqueueSnackbar('Por favor ingrese la contraseña maestra', { variant: 'error' });
                  return;
                }
                handleSubmit(masterPassword);
              }}
              variant="contained"
              className="button_1"
            >
              Confirmar
            </Button>
          </div>
        </Dialog>
      </>
  );
}